import React, { createContext, useContext, useEffect } from "react";

import { setProctorOverridesFromPrforceGroups } from "../proctor/proctorOverrideStore";
import type { NativeContextRNProps } from "./NativeContextRNProps";

/**
 * Context for data passed from native apps
 *
 * This context provides access to values that are passed from the native iOS/Android apps
 * to the React Native layer. It centralizes native-provided data and makes it easy to
 * migrate to fetching data within RN in the future.
 */
const NativeContext = createContext<NativeContextRNProps | undefined>(undefined);

export function useNativeContext(): NativeContextRNProps | undefined {
  return useContext(NativeContext);
}

export interface NativeContextProviderProps {
  nativeContext?: NativeContextRNProps;
  children: React.ReactNode;
}

/**
 * Provider for native context values
 *
 * Wraps the component tree and provides native-sourced data to child components.
 * If no nativeContext is provided, components using useNativeContext will receive undefined.
 */
export function NativeContextProvider({ nativeContext, children }: NativeContextProviderProps) {
  // Seed the imperative Proctor-override store so non-React readers (e.g. the
  // synchronous `getAccountStatusSync` path in `tareCodegenMigration`) honor
  // locally-forced buckets, mirroring the `prforceGroups` override the generated
  // `…ProctorProvider` applies for React consumers.
  useEffect(() => {
    setProctorOverridesFromPrforceGroups(nativeContext?.prforceGroups);
  }, [nativeContext?.prforceGroups]);

  return <NativeContext.Provider value={nativeContext}>{children}</NativeContext.Provider>;
}
